import {useI18n} from '@shopify/react-i18n';
import {Spinner, Icon} from '@shopify/polaris';
import {classNames} from '@shopify/css-utilities';
import {CheckCircleIcon, XCircleIcon} from '@shopify/polaris-icons';
import {Image as ImageType} from '@web-utilities/types';

import {Mode} from '../../types';

import styles from './Image.scss';

export interface Props {
  image: ImageType;
  onImageClick(image: ImageType): void;
  loading?: boolean;
  selected?: boolean;
  disabled?: boolean;
  mode?: Mode;
}

function truncateFileName(aString: string) {
  return aString.substring(aString.lastIndexOf('/') + 1).split('?')[0];
}

function Image({
  image,
  selected,
  disabled,
  loading,
  onImageClick,
  mode,
}: Props) {
  const [i18n] = useI18n();
  const {src, altText} = image;

  if (loading) {
    return (
      <span className={styles.Image}>
        <img className={styles.ImageElement} src={src} alt={altText || ''} />
        <span className={styles.ImageOverlay}>
          <span className={styles.ImageText}>{truncateFileName(src)}</span>
          <Spinner />
        </span>
      </span>
    );
  }

  function imageCallback() {
    if (disabled) {
      return;
    }

    return onImageClick(image);
  }

  /* eslint-disable jsx-a11y/role-supports-aria-props */
  return mode === 'unselect' ? (
    <div
      className={classNames(
        styles.Image,
        selected && styles['Image-active'],
        disabled && styles['Image-disabled'],
      )}
    >
      <img className={styles.ImageElement} src={src} alt={altText || ''} />
      {selected && !disabled && (
        <button
          type="button"
          className={styles.Unselect}
          onClick={imageCallback}
          aria-selected={selected}
          aria-label={i18n.translate('unselectImage')}
          disabled={disabled}
        >
          <Icon source={XCircleIcon} tone="subdued" />
        </button>
      )}
    </div>
  ) : (
    <button
      type="button"
      className={classNames(
        styles.ImageButton,
        selected && styles['Image-active'],
        disabled && styles['Image-disabled'],
      )}
      onClick={imageCallback}
      aria-selected={selected}
      aria-label={altText || ''}
      disabled={disabled}
    >
      <img className={styles.ImageElement} src={src} alt={altText || ''} />
      {selected && (
        <div className={styles.Checkmark}>
          <Icon source={CheckCircleIcon} tone="subdued" />
        </div>
      )}
    </button>
  );
  /* eslint-enable jsx-a11y/role-supports-aria-props */
}

export default Image;
